import { FC, useMemo, useState } from "react"
import { Formik, Form, Field } from "formik"
import { object, string } from "yup"
import { Card, ImageUploader, PrimaryBtn } from "../../../common/ui"
import Select from "../../../common/ui/form/select"
import Input from "../../../common/ui/form/input"
import WysiwygEditor from "../../../common/ui/form/WysiwygEditor"
import Autocomplete from "../../../common/ui/form/autocomplete"
import CurrencyInput from "../../../common/ui/form/currency-input"
import {
  useCreateOrganizationMutation,
  useFindObOrgQuery
} from "../../../redux/queries"
import { useAppDispatch } from "../../../common/hooks"
import { useDebouncedCallback } from "../../../common/hooks/use-debounced-callback"
import { pushToast } from "../../../redux/slices/notifications"
import { CreateOrgPayload, CreateOrgResponse, APIError } from "../../../types"
import {
  PricingEngineType,
  PRICING_ENGINE_LABELS
} from "../../../types/pricing-engine"

interface Props {
  onSuccess: (orgId: string, orgName: string) => void
}

interface FormValues {
  name: string
  website: string
  about: string
  logo_url: string
  primary_email: string
  setup_fee_amount: string
  pricing_engine: string
  ob_biz_channel: string
}

const validationSchema = object({
  name: string().required("Organization name is required"),
  website: string(),
  about: string(),
  logo_url: string().required("Logo is required"),
  primary_email: string()
    .email("Invalid email address")
    .required("Billing email is required"),
  setup_fee_amount: string(),
  pricing_engine: string().required("Pricing engine is required"),
  ob_biz_channel: string()
})

const initialValues: FormValues = {
  name: "",
  website: "",
  about: "",
  logo_url: "",
  primary_email: "",
  setup_fee_amount: "",
  pricing_engine: "",
  ob_biz_channel: ""
}

const NewCustomerForm: FC<Props> = ({ onSuccess }) => {
  const dispatch = useAppDispatch()
  const [createOrganization, { isLoading }] = useCreateOrganizationMutation()
  const [obQuery, setObQuery] = useState("")
  const [debouncedObQuery, setDebouncedObQuery] = useState("")

  const debouncedSetQuery = useDebouncedCallback((val: string) => {
    setDebouncedObQuery(val)
  }, 300)

  const handleObQueryChange = (val: string) => {
    setObQuery(val)
    debouncedSetQuery(val)
  }

  // Seed the server-side cache on page load
  useFindObOrgQuery({ name: "zzz" })

  const { data: obOrgData, isFetching: isObLoading } = useFindObOrgQuery(
    { name: debouncedObQuery },
    { skip: !debouncedObQuery }
  )

  const obOptions = useMemo(() => {
    if (!obOrgData?.length) return []
    return obOrgData.map(item => ({
      id: item.index.toString(),
      text: `${item.name} (${item.index})`
    }))
  }, [obOrgData])

  const handleSubmit = async (values: FormValues) => {
    const payload: CreateOrgPayload = {
      name: values.name,
      website: values.website,
      about: values.about,
      logo_url: values.logo_url,
      primary_email: values.primary_email,
      listing_access: false,
      setup_fee_amount: values.setup_fee_amount || undefined,
      pricing_engine: values.pricing_engine || undefined,
      ob_biz_channel: values.ob_biz_channel || undefined
    }

    try {
      const result = await createOrganization(payload).unwrap()
      const response = result as CreateOrgResponse | APIError

      if ("status" in response && response.status === "error") {
        dispatch(
          pushToast({
            msg:
              (response as APIError).message || "Failed to create organization",
            theme: "danger",
            timeout: 5000
          })
        )
        return
      }

      if ("org_id" in response) {
        dispatch(
          pushToast({
            msg: "Organization created successfully",
            theme: "success",
            timeout: 3000
          })
        )
        onSuccess(response.org_id, values.name)
      }
    } catch (error) {
      dispatch(
        pushToast({
          msg: "Failed to create organization",
          theme: "danger",
          timeout: 5000
        })
      )
    }
  }

  return (
    <Card>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, errors, touched }) => (
          <Form>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo <span className="text-red-500">*</span>
                </label>
                <ImageUploader
                  isFullRounded={false}
                  imageUrl={values.logo_url}
                  onChangeImageUrl={url => setFieldValue("logo_url", url)}
                />
                {errors.logo_url && touched.logo_url && (
                  <p className="mt-1 text-sm text-red-600">{errors.logo_url}</p>
                )}
              </div>

              <Input
                label="Organization name"
                name="name"
                placeholder="Ex. Fivestar Home Loans"
                required
              />

              <Input
                label="Billing email"
                name="primary_email"
                type="email"
                placeholder="billing@example.com"
                required
              />

              <Input
                label="Website URL"
                name="website"
                placeholder="Ex. www.example.com"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  About organization
                </label>
                <WysiwygEditor
                  name="about"
                  label=""
                  initialValue={values.about}
                  setFieldValue={val => setFieldValue("about", val)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Setup fee amount
                </label>
                <Field name="setup_fee_amount">
                  {({ field }: { field: any }) => (
                    <CurrencyInput
                      field={field}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      setFormField={setFieldValue}
                    />
                  )}
                </Field>
              </div>

              <Select
                label="Pricing engine"
                name="pricing_engine"
                required
                data={Object.entries(PRICING_ENGINE_LABELS).map(
                  ([id, name]) => ({ id, name })
                )}
                selected={values.pricing_engine}
                setSelected={(val: string) => {
                  setFieldValue("pricing_engine", val)
                  if (val !== "optimal_blue") {
                    setFieldValue("ob_biz_channel", "")
                  }
                }}
                error={
                  touched.pricing_engine && errors.pricing_engine
                    ? errors.pricing_engine
                    : undefined
                }
              />

              {values.pricing_engine === "optimal_blue" && (
                <Autocomplete
                  name="ob_biz_channel"
                  label="OptimalBlue business channel"
                  query={obQuery}
                  onChangeQuery={handleObQueryChange}
                  onChange={val => setFieldValue("ob_biz_channel", val.id)}
                  options={obOptions}
                  isLoading={isObLoading}
                />
              )}

              <div className="pt-4">
                <PrimaryBtn type="submit" disabled={isLoading}>
                  {isLoading ? "Creating..." : "Create Organization"}
                </PrimaryBtn>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </Card>
  )
}

export default NewCustomerForm
